import React, {createContext, useCallback, useContext, useState} from 'react'

export interface SpeechToSpeechContextModel {
  isWidgetOpen: boolean
  openWidget: () => void
  closeWidget: () => void
  toggleWidget: () => void
  // True while WidgetInCall is mounted (live call in progress). Used to
  // suppress the corner close button and outside-click dismissal so a call
  // can't be accidentally killed.
  isCallActive: boolean
  setCallActive: (active: boolean) => void
}

const SpeechToSpeechContext = createContext<SpeechToSpeechContextModel | null>(null)

const SpeechToSpeechProvider: React.FC<{children: React.ReactNode}> = ({children}) => {
  const [isWidgetOpen, setIsWidgetOpen] = useState(false)
  const [isCallActive, setIsCallActive] = useState(false)

  const openWidget = useCallback(() => setIsWidgetOpen(true), [])
  const closeWidget = useCallback(() => setIsWidgetOpen(false), [])
  const toggleWidget = useCallback(() => setIsWidgetOpen((prev) => !prev), [])
  const setCallActive = useCallback((active: boolean) => setIsCallActive(active), [])

  const value: SpeechToSpeechContextModel = {
    isWidgetOpen,
    openWidget,
    closeWidget,
    toggleWidget,
    isCallActive,
    setCallActive,
  }

  return (
    <SpeechToSpeechContext.Provider value={value}>
      {children}
    </SpeechToSpeechContext.Provider>
  )
}

function useSpeechToSpeech(): SpeechToSpeechContextModel {
  const ctx = useContext(SpeechToSpeechContext)
  if (!ctx) {
    throw new Error('useSpeechToSpeech must be used within SpeechToSpeechProvider')
  }
  return ctx
}

export {SpeechToSpeechProvider, useSpeechToSpeech}
